import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, Mail, Globe,
  Building, Users, TrendingUp, Shield, Star
} from 'lucide-react';
import { useInView } from '../components/UI';

const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const distinction = [
  {
    type: 'The AZT Token Round',
    sub: 'Open, standardised, self-directed.',
    points: [
      'Open to all participants',
      'Fixed price and published terms',
      'Entry from as little as $10',
      'A platform token backed by real assets',
    ],
    cta: null,
    dark: false,
  },
  {
    type: 'The Anchor Partnership',
    sub: 'Selective, custom, founder-led.',
    points: [
      'By introduction or direct enquiry',
      'Terms structured individually',
      'Funding from $100K, or strategic value by relevance',
      'A direct line to the founding team',
    ],
    cta: 'Begin a conversation',
    dark: true,
  },
];

const doors = [
  {
    door: 'Door One',
    title: 'Funding Partners',
    sub: 'Anchor commitments from $100,000',
    img: '/images/bike2.png',
    desc: 'For partners whose primary contribution is money. An anchor commitment funds a meaningful share of Azura\'s founding phase, from fleet deployment to the battery-swap network, and carries a direct, clearly defined position in the company.',
    note: '$100K is the entry point, not the ceiling. Larger strategic commitments are welcomed and carry deeper involvement. Terms scale with the nature of the commitment and are discussed individually.',
  },
  {
    door: 'Door Two',
    title: 'Strategic & Influence Partners',
    sub: 'Considered by relevance, not by cheque',
    img: '/images/bike7.png',
    desc: 'For partners whose value is access, standing, expertise, or reach. A single introduction, a regulatory relationship, a sector network, or a credible public association can advance Azura further than money alone at this stage.',
    note: 'This path is considered on the strategic value you bring to Azura\'s specific path, in Nigeria, Africa or any location, in target markets, or across the EV and manufacturing sector. Relevance matters more than prominence.',
  },
];

const gains = [
  { num: '01', title: 'A direct line to the founders', desc: 'Anchor partners work directly with the founding team, not through a portal or an intermediary. The relationship is personal, ongoing, and built on access.' },
  { num: '02', title: 'Early position, ahead of the public rounds', desc: "Anchor partners take their position before the wider market, at the founding phase, when the company's trajectory is still being set rather than priced in." },
  { num: '03', title: 'A voice in the direction', desc: 'Depending on the nature of the commitment, anchor partners can hold strategic input into how Azura grows, where it expands, and how the Sankofa vision unfolds.' },
  { num: '04', title: "A stake in Africa's industrial future", desc: "This is a legacy position, not one built for quick income. Anchor partners help build Africa's first local EV manufacturer and carry their association with that story." },
];

const categories = [
  { icon: Shield, title: 'Government & Regulatory', desc: 'Influence and access at state or national level, in Nigeria or in target expansion markets, where policy and permission shape the path.' },
  { icon: Building, title: 'Industry & Sector Leaders', desc: 'Standing and expertise in automotive, energy, logistics, fleet operations, or manufacturing — the sectors Azura must master.' },
  { icon: TrendingUp, title: 'Development & Institutional Finance', desc: 'Relationships with DFIs, climate funds, ESG capital, and blended finance facilities that unlock the next tier of growth.' },
  { icon: Users, title: 'Diaspora & Community Leaders', desc: 'Standing, reach, and trust within communities and networks that give a young company credibility and a route to market.' },
  { icon: Globe, title: 'Media & Public Influence', desc: 'Reach and credibility in media and public life, where a credible voice can carry the Azura story further and faster.' },
  { icon: Star, title: 'Other Strategic Value', desc: "If you bring something relevant that does not fit a category above, Azura would still like to hear it. Relevance is the only real test." },
];

const steps = [
  { num: '1', title: 'Tell us what you bring', desc: 'Reach out directly and state plainly whether you bring money, strategic value, or both, and in what area. Specifics help us respond well.' },
  { num: '2', title: 'A direct conversation', desc: 'If there is a fit, you speak directly with the founding team. This is where the structure, the terms, and the relationship are shaped.' },
  { num: '3', title: 'A position, structured to you', desc: 'If both sides see the fit, we structure an anchor position tailored to the nature and scale of your commitment.' },
];

/* ══ PARTNERS PAGE ══ */
const Partners: React.FC = () => {
  const { ref: distRef, inView: distInView } = useInView();
  const { ref: doorsRef, inView: doorsInView } = useInView();
  const { ref: gainsRef, inView: gainsInView } = useInView();
  const { ref: catRef, inView: catInView } = useInView();
  const { ref: stepsRef, inView: stepsInView } = useInView();

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
              <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
              <span className="text-white/70">/</span>
              <span className="text-accent-amber text-xs font-semibold">Partners</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
              Anchor Partners
            </h1>

            <p className="max-w-2xl text-white/70 text-base leading-8 mb-10">
              A small number of partners will help shape what Azura becomes. Beyond the public token round, Azura works directly with a select group of anchor partners: individuals who bring money, influence, or both to the founding phase of Africa's electric transport systems.
            </p>
{/* 
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'From $100K', sub: 'Anchor funding commitment' },
                { label: 'Direct', sub: 'Founder-level relationship' },
                { label: 'Selective', sub: 'Limited partners, by relevance' },
              ].map(b => (
                <div key={b.label} className="inline-flex items-center gap-2.5 rounded-full bg-white/10 border border-white/10 px-5 py-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-amber" />
                  <span className="font-figtree font-bold text-white text-xs">{b.label}</span>
                  <span className="text-white/70 font-exo text-xs">· {b.sub}</span>
                </div>
              ))}
            </div> */}
          </div>

        </div>
      </section>

      {/* ══ THE DISTINCTION ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={distRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${distInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Distinction</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              The token round is open to everyone
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {distinction.map((d, i) => (
              <div key={d.type}
                className={`rounded-2xl p-8 border relative overflow-hidden transition-all duration-700 ${d.dark ? 'bg-bg-dark border-bg-dark' : 'bg-white border-border shadow-card'} ${distInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                <span className="absolute bottom-5 right-6 font-figtree font-black text-[5rem] leading-none text-white/[0.04] select-none pointer-events-none">{String(i + 1).padStart(2, '0')}</span>

                <p className="text-xs text-accent-amber uppercase tracking-widest mb-2">{d.type}</p>
                <h3 className={`font-figtree font-black text-xl mb-5 ${d.dark ? 'text-white' : 'text-bg-dark'}`}>{d.sub}</h3>

                <div className="space-y-3 mb-6">
                  {d.points.map(p => (
                    <div key={p} className="flex items-start gap-2.5">
                      <CheckCircle2 size={14} className={`shrink-0 mt-0.5 ${d.dark ? 'text-accent-amber' : 'text-bg-dark'}`} />
                      <span className={`font-exo text-sm leading-relaxed ${d.dark ? 'text-white/60' : 'text-neutral-600'}`}>{p}</span>
                    </div>
                  ))}
                </div>

                {d.cta && (
                  <a href="mailto:invest@azuramobility.com"
                    className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-5 py-2.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all ">
                    {d.cta} <ArrowRight size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA 2 ══ */}
      <section className="py-16 px-6 bg-bg-dark border-t-4 border-accent-amber text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-2xl md:text-3xl text-white tracking-tight mb-3">
            No introduction needed. <span className="text-accent-amber italic">Buy AZT directly.</span>
          </h2>
          <p className="font-exo text-white/70 text-sm leading-relaxed mb-6 max-w-xl mx-auto">
            The public Strategic Round is open to anyone from as little as $20 — no conversation required.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
            Buy AZT <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* ══ TWO DOORS ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={doorsRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${doorsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Two Ways to Anchor</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              Money, influence, <span className="text-accent-amber italic">or both.</span>
            </h2>
            <p className="font-exo text-neutral-600 text-sm leading-relaxed mt-4 max-w-2xl">
              Azura recognises that the most valuable partners do not always arrive with the largest cheque. Some bring money. Some bring access, standing, and reach. Both matter at this stage, and both have a door.
            </p>
          </div>

          <div className="space-y-8">
            {doors.map((d, i) => (
              <div key={d.door}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center transition-all duration-700 ${doorsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}>


                <div className={`lg:col-span-4 ${i % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-card border border-border group">
                    <img src={d.img} alt={d.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = '#E5E3DC'; }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <p className="text-xs text-accent-amber/70 uppercase tracking-widest mb-1">{d.door}</p>
                      <p className="font-figtree font-black text-white text-lg">{d.title}</p>
                    </div>
                  </div>
                </div>

                {/* text */}
                <div className={`lg:col-span-8 ${i % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="bg-white border border-border rounded-2xl p-8 shadow-card relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                    <p className="text-xs text-accent-amber uppercase tracking-widest mb-2">{d.door}</p>
                    <h3 className="font-figtree font-black text-2xl text-bg-dark mb-1">{d.title}</h3>
                    <p className="text-xs text-neutral-400 uppercase tracking-widest mb-5">{d.sub}</p>
                    <p className="font-exo text-sm text-neutral-600 leading-relaxed mb-5">{d.desc}</p>
                    <div className="flex gap-3 p-5 bg-neutral-50 rounded-xl border border-border">
                      <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
                      <p className="font-exo text-xs text-bg-dark leading-relaxed">{d.note}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHAT AN ANCHOR PARTNER GAINS ══ */}
      <section className="py-20 px-6 bg-bg-dark text-white relative overflow-hidden border-t-4 border-accent-amber">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute bottom-10 left-10 w-40 h-24 opacity-[0.08]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div ref={gainsRef} className="max-w-7xl mx-auto relative z-10">
          <div className={`mb-14 transition-all duration-700 ${gainsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">What an Anchor Partner Gains</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight">
              Position, access, and a stake<br />
              <span className="text-accent-amber italic">in a continental story.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {gains.map((g, i) => (
                <div key={g.num}
                  className={`bg-white/[0.04] border border-white/[0.08] rounded-2xl p-7 relative overflow-hidden transition-all duration-700 ${gainsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}>
                  <span className="absolute top-4 right-5 font-figtree font-black text-4xl leading-none text-white/[0.05] select-none">{g.num}</span>
                  <div className="w-8 h-8 rounded-full bg-accent-amber/10 border border-accent-amber/20 flex items-center justify-center mb-5">
                    <div className="w-2 h-2 rounded-full bg-accent-amber" />
                  </div>
                  <h3 className="font-figtree font-bold text-sm text-white mb-2 leading-snug">{g.title}</h3>
                  <p className="font-exo text-xs text-white/70 leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] shadow-dark border border-white/10 group">
                <img src="/images/bike3.png" alt="Anchor partners" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.03]"
                  onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = '#E5E3DC'; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-figtree font-black text-2xl text-white mb-1">This is a relationship.</p>
                  <p className="font-exo text-sm text-white/70 leading-relaxed">Not a transaction. Not a portal. A direct position with the founding team.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ WHAT AZURA LOOKS FOR ══ */}
      <section className="py-20 px-6 bg-white border-t border-border">
        <div ref={catRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${catInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">What Azura Looks For</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-3">
              Money is one kind of value.<br />
              <span className="text-accent-amber italic">These are the others.</span>
            </h2>
            <p className="font-exo text-neutral-600 text-sm leading-relaxed max-w-2xl">
              Azura welcomes anchor partners across these categories. If you bring relevance to Azura's path, in any form, there is a place for the conversation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={c.title}
                  className={`bg-white border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden ${catInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                  <div className="w-11 h-11 rounded-xl bg-neutral-50 border border-border flex items-center justify-center text-bg-dark mb-5 group-hover:bg-accent-amber group-hover:border-accent-amber transition-all duration-300">
                    <Icon size={18} />
                  </div>
                  <h3 className="font-figtree font-black text-sm text-bg-dark mb-2 leading-snug">{c.title}</h3>
                  <p className="font-exo text-xs text-neutral-500 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 relative rounded-2xl overflow-hidden aspect-[21/9] border border-border shadow-card">
            <img src="/images/bg3.jpg" alt="Building Africa's industrial future" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 via-transparent to-transparent" />          </div>
        </div>
      </section>

      {/* ══ TERMS ══ */}
      <section className="py-20 px-6 bg-bg-cream border-t border-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Terms</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-5">
              Structured individually,<br />
              <span className="text-accent-amber italic">by conversation.</span>
            </h2>
            <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-5">
              An anchor partnership is a direct, clearly defined position in Azura, distinct from the public token round. The exact structure is tailored to the nature and scale of what each partner brings, and is discussed individually rather than published.
            </p>
            <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-5">
              This is deliberate. A custom relationship cannot be reduced to a fixed tier, and the terms that suit a funding partner are not the terms that suit a partner bringing access or reach. We would rather shape something that fits than offer something generic.
            </p>
            <div className="p-5 rounded-2xl bg-white border border-border shadow-card">
              <div className="flex gap-4">
                <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
                <p className="font-exo text-sm text-bg-dark leading-relaxed">Anchor partners take a more involved, more advantageous position than the public round offers. The specifics are the first thing we discuss when you reach out.</p>
              </div>
            </div>
          </motion.div>

          {/* How to reach us + form */}
          <div className="lg:col-span-7">
            <div ref={stepsRef} className={`mb-8 transition-all duration-700 ${stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">How to Reach Us</span>
              </div>

              <div className="relative mb-8">
                <div className="absolute left-5 top-5 bottom-5 w-px bg-gradient-to-b from-accent-amber/60 via-accent-amber/20 to-transparent" />
                <div className="space-y-6">
                  {steps.map((st, i) => (
                    <div key={st.num}
                      className={`flex gap-5 items-start pl-12 relative transition-all duration-700 ${stepsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                      style={{ transitionDelay: `${i * 100}ms` }}>
                      <div className="absolute left-0 w-10 h-10 rounded-full bg-bg-dark flex items-center justify-center font-bold text-accent-amber text-xs shrink-0 z-10">
                        {st.num}
                      </div>
                      <div>
                        <h4 className="font-figtree font-bold text-sm text-bg-dark mb-1">{st.title}</h4>
                        <p className="font-exo text-xs text-neutral-500 leading-relaxed">{st.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Direct email contact */}
              <div className="p-6 rounded-2xl bg-white border border-border shadow-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-accent-amber/10 border border-accent-amber/20 flex items-center justify-center shrink-0">
                    <Mail size={14} className="text-bg-dark" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-bg-dark/40">Direct Email</p>
                </div>
                <a href="mailto:partners@azuramobility.com"
                  className="font-figtree font-bold text-base text-bg-dark hover:text-accent-amber transition-colors block mb-1">
                  partners@azuramobility.com
                </a>
                <p className="font-exo text-xs text-neutral-500 leading-relaxed">
                  State plainly what you bring and we will respond directly.
                </p>
              </div>

              <div className="mt-6 relative rounded-2xl overflow-hidden aspect-video border border-border shadow-card">
                <img src="/images/impact3.webp" alt="Azura founding team" className="w-full h-full object-cover" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-white tracking-tight mb-4">
            Not ready for an anchor position? <span className="text-accent-amber italic">Start with AZT.</span>
          </h2>
          <p className="font-exo text-white/55 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            The public AZT Strategic Round is open to everyone, from as little as $20. It opens October 15.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
            Invest Now <ArrowRight size={13} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default Partners;